// CalendarView.js - Calendar Page for Viewing Events

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert,
  Stack, IconButton, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Link as LinkIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { calendarService } from './activityCommService';
import { formatDateTime, formatDate } from './activityCommEnums';

const SOURCE_COLORS = {
  manual: '#1976d2',
  google: '#4caf50',
  outlook: '#0078d4'
};

const SOURCE_LABELS = {
  manual: 'Manual',
  google: 'Google',
  outlook: 'Outlook'
};

const CalendarView = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [viewMode, setViewMode] = useState(0); // 0=Month, 1=Week, 2=Day
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const today = new Date();
  const [startDate, setStartDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0]
  );

  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    attendees: '',
    meeting_link: '',
    contact_id: ''
  });

  useEffect(() => {
    document.title = 'Calendar | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadEvents();
    }
  }, [canView, startDate, endDate]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await calendarService.getEvents({ start_date: startDate, end_date: endDate });
      setEvents(res.data || res || []);
    } catch (err) {
      enqueueSnackbar('Failed to load events', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.start_time || !newEvent.end_time) {
      enqueueSnackbar('Title, start time, and end time are required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      let attendeesJson = [];
      if (newEvent.attendees.trim()) {
        try {
          attendeesJson = JSON.parse(newEvent.attendees);
        } catch {
          attendeesJson = newEvent.attendees.split(',').map(e => ({ email: e.trim() }));
        }
      }

      await calendarService.createEvent({
        ...newEvent,
        attendees: attendeesJson,
        contact_id: newEvent.contact_id ? parseInt(newEvent.contact_id, 10) : null
      });
      enqueueSnackbar('Event created successfully', { variant: 'success' });
      setCreateOpen(false);
      setNewEvent({ title: '', description: '', start_time: '', end_time: '', location: '', attendees: '', meeting_link: '', contact_id: '' });
      loadEvents();
    } catch (err) {
      enqueueSnackbar('Failed to create event', { variant: 'error' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleViewChange = (event, newValue) => {
    setViewMode(newValue);
    const now = new Date();
    if (newValue === 0) {
      setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
      setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);
    } else if (newValue === 1) {
      const dayOfWeek = now.getDay();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - dayOfWeek);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      setStartDate(weekStart.toISOString().split('T')[0]);
      setEndDate(weekEnd.toISOString().split('T')[0]);
    } else {
      setStartDate(now.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    }
  };

  const navigateDate = (direction) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const offset = direction === 'next' ? 1 : -1;

    if (viewMode === 0) {
      start.setMonth(start.getMonth() + offset);
      end.setMonth(end.getMonth() + offset);
      const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    } else if (viewMode === 1) {
      start.setDate(start.getDate() + offset * 7);
      end.setDate(end.getDate() + offset * 7);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    } else {
      start.setDate(start.getDate() + offset);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(start.toISOString().split('T')[0]);
    }
  };

  // Group events by date
  const groupedEvents = {};
  const sortedEvents = [...(Array.isArray(events) ? events : [])].sort(
    (a, b) => new Date(a.start_time) - new Date(b.start_time)
  );
  sortedEvents.forEach(event => {
    const dateKey = new Date(event.start_time).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    if (!groupedEvents[dateKey]) groupedEvents[dateKey] = [];
    groupedEvents[dateKey].push(event);
  });

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/activity-dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Calendar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your events and meetings
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {canModify && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New Event
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/calendar/connections`)}
          >
            Connections
          </Button>
        </Stack>
      </Box>

      {/* View Toggle and Navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Tabs value={viewMode} onChange={handleViewChange}>
            <Tab label="Month" />
            <Tab label="Week" />
            <Tab label="Day" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigateDate('prev')}>
              <ChevronLeftIcon />
            </IconButton>
            <TextField
              type="date"
              size="small"
              label="From"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <IconButton onClick={() => navigateDate('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Stack direction="row" spacing={1}>
            {Object.entries(SOURCE_LABELS).map(([key, label]) => (
              <Chip
                key={key}
                icon={<CircleIcon sx={{ fontSize: 10, color: `${SOURCE_COLORS[key]} !important` }} />}
                label={label}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Events List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(groupedEvents).length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CalendarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No events found</Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your date range or create a new event.
          </Typography>
        </Paper>
      ) : (
        Object.entries(groupedEvents).map(([dateLabel, dateEvents]) => (
          <Paper key={dateLabel} sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>{dateLabel}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={140}>Time</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell width={100}>Attendees</TableCell>
                    <TableCell width={180}>Location</TableCell>
                    <TableCell width={140}>Meeting Link</TableCell>
                    <TableCell width={90}>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dateEvents.map((event, idx) => {
                    const source = (event.source || 'manual').toLowerCase();
                    return (
                      <TableRow
                        key={event.id || idx}
                        sx={{
                          borderLeft: `4px solid ${SOURCE_COLORS[source] || SOURCE_COLORS.manual}`,
                          '&:hover': { bgcolor: 'grey.50' }
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {new Date(event.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              {' - '}
                              {new Date(event.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{event.title}</Typography>
                          {event.description && (
                            <Typography variant="caption" color="text.secondary">{event.description}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {event.attendees && (
                            <Chip
                              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                              label={Array.isArray(event.attendees) ? event.attendees.length : 0}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {event.location && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption">{event.location}</Typography>
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          {event.meeting_link && (
                            <Button
                              size="small"
                              startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                              href={event.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              Join
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={SOURCE_LABELS[source] || 'Manual'}
                            size="small"
                            sx={{
                              bgcolor: `${SOURCE_COLORS[source] || SOURCE_COLORS.manual}15`,
                              color: SOURCE_COLORS[source] || SOURCE_COLORS.manual,
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Event</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Start Time"
                  type="datetime-local"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Time"
                  type="datetime-local"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  value={newEvent.end_time}
                  onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              label="Location"
              fullWidth
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
            />
            <TextField
              label="Attendees (comma-separated emails or JSON array)"
              fullWidth
              value={newEvent.attendees}
              onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
              helperText='e.g. john@example.com, jane@example.com'
            />
            <TextField
              label="Meeting Link"
              fullWidth
              value={newEvent.meeting_link}
              onChange={(e) => setNewEvent({ ...newEvent, meeting_link: e.target.value })}
            />
            <TextField
              label="Contact ID (optional)"
              fullWidth
              type="number"
              value={newEvent.contact_id}
              onChange={(e) => setNewEvent({ ...newEvent, contact_id: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateEvent} disabled={saving}>
            {saving ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarView;
